import { AuthorizationError, Collaboration, CollaboratorError, ContainersPermission, InvalidInputError } from "@back4app2/sdk";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import TextInput from "../FormInputField/TextInput/TextInput";
import Button from "../Button";
import back4app2 from "../../back4app2";
import CollaboratorPermissionModal, { ContainersPermissionInput } from "./CollaboratorPermissionModal";
import { CollaboratorsList } from "./CollaboratorsList";
import { RemoveCollaboratorModal } from "./RemoveCollaboratorModal";
import { ReactComponent as StatusSuccessSVG } from '../../assets/images/status-success.svg';
import LoadingSpinner from "../LoadingSpinner";
import { User } from "@back4app2/sdk";
import { BACK4APP_DOT_COM_DASHBOARD_URL } from "../../settings";

export interface CollaboratorsProps {
  appId: string;
  isOwner: boolean;
  currentUser: User;
}
const validateEmail = (email: string): string => {
  if (!email || email.trim() === '') {
    return 'Email is required';
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.trim())) {
    return 'Please enter a valid email address';
  }
  
  return '';
}; 

const defaultFeaturesPermissions: ContainersPermissionInput = {
  Overview: 'Read',
  Logs: 'Read',
  Deployments: 'Read',
  Metrics: 'Read',
  Settings: {
    General: 'Read',
    BuildAndDeploy: 'Write',
    EnvVars: 'Write',
    Domain: 'Write',
    HealthAndAlert: 'Write',
  },
  Actions: {
    Deploy: 'Write',
  },
};

const Collaborators = (props: CollaboratorsProps) => {
  const [newCollaboratorEmail, setNewCollaboratorEmail] = useState('');
  const [disableAddButton, setDisableAddButton] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [showInvite, setShowInvite] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [customPermissions] = useState<ContainersPermissionInput | undefined>(undefined);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaboration[]>([]);
  const [isLoadingCollaborators, setIsLoadingCollaborators] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [collaboratorToRemove, setCollaboratorToRemove] = useState<Collaboration | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string>('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [collaboratorToEdit, setCollaboratorToEdit] = useState<Collaboration | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editError, setEditError] = useState<string>('');

  useEffect(() => {
    setIsLoadingCollaborators(true);
    
    const subscription = back4app2.subscribeToCollaborators(
      props.appId,
      (error, snapshot) => {
        if (error) {
          console.error('Failed to fetch collaborators:', error);
          setIsLoadingCollaborators(false);
        } else if (snapshot) {
          setCollaborators(snapshot);
          setIsLoadingCollaborators(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [props.appId]);
  
  const handleAddCollaborator = async () => {
    setDisableAddButton(true);
    setEmailError('');
    setShowInvite(false);
    const trimmedEmail = newCollaboratorEmail.trim();
    const error = validateEmail(trimmedEmail);
    
    if (error) {
      setEmailError(error);
      return;
    }

    try {
      const emailValid = await back4app2.validateEmail(trimmedEmail);
      setIsEmailValid(emailValid);
      if (!emailValid) {
        setShowInvite(true);
        return;
      } else {
        // Email is valid, show permission modal directly
        setShowPermissionModal(true);
      }
    } catch (error) {
      if (error instanceof InvalidInputError) {
        setEmailError(error.message);
        return;
      }
      setEmailError('Something went wrong!');
      return;
    } finally {
      setDisableAddButton(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewCollaboratorEmail(value);
    // Clear error when user starts typing
    if (emailError) {
      setEmailError('');
    }
    if (showInvite) {
      setShowInvite(false);
    }
    if (disableAddButton) {
      setDisableAddButton(false);
    }
  };

  const convertToContainersPermissionInput = (permissions: ContainersPermission): ContainersPermissionInput => {
    return {
      Overview: permissions.Overview,
      Logs: permissions.Logs,
      Deployments: permissions.Deployments,
      Metrics: permissions.Metrics,
      Settings: {
        General: permissions.Settings.General,
        BuildAndDeploy: permissions.Settings.BuildAndDeploy as 'None' | 'Read' | 'Write',
        EnvVars: permissions.Settings.EnvVars as 'None' | 'Read' | 'Write',
        Domain: permissions.Settings.Domain as 'None' | 'Read' | 'Write',
        HealthAndAlert: permissions.Settings.HealthAndAlert as 'None' | 'Read' | 'Write',
      },
      Actions: {
        Deploy: permissions.Actions.Deploy,
      },
    };
  };

  const handleEditCollaborator = (collaborator: Collaboration) => {
    setCollaboratorToEdit(collaborator);
    setEditError('');
    setShowEditModal(true);
  }

  const handleSaveEdit = async (permissions: ContainersPermissionInput) => {
    if (!collaboratorToEdit) return;

    setIsEditing(true);
    setEditError('');

    try {
      await back4app2.updateCollaboratorPermission(
        props.appId,
        collaboratorToEdit.email,
        permissions as ContainersPermission
      );

      // List will be updated automatically via subscription

      // Show success message
      toast.success('Collaborator permissions updated successfully!', {
        className: "bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />
      });

      // Close modal
      setShowEditModal(false);
      setCollaboratorToEdit(null);
    } catch (error) {
      if (error instanceof CollaboratorError || error instanceof AuthorizationError) {
        setEditError(error.message);
      } else {
        setEditError('Failed to update collaborator permissions. Please try again.');
      }
    } finally {
      setIsEditing(false);
    }
  }

  const handleCloseEditModal = () => {
    if (!isEditing) {
      setShowEditModal(false);
      setCollaboratorToEdit(null);
      setEditError('');
    }
  }

  const handleRemoveCollaborator = (collaborator: Collaboration) => {
    setCollaboratorToRemove(collaborator);
    setRemoveError('');
    setShowRemoveModal(true);
  }

  const handleConfirmRemove = async () => {
    if (!collaboratorToRemove) return;

    setIsRemoving(true);
    setRemoveError('');

    try {
      await back4app2.removeCollaborator(props.appId, collaboratorToRemove.email);
      
      // Show success message
      toast.success('Collaborator removed successfully!', {
        className: "bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />
      });

      if (collaboratorToRemove.email === props.currentUser.username) {
        window.location.href = `${BACK4APP_DOT_COM_DASHBOARD_URL}/apps`;
      }
      
      // Close modal
      setShowRemoveModal(false);
      setCollaboratorToRemove(null);
    } catch (error) {
      if (error instanceof CollaboratorError || error instanceof AuthorizationError) {
        setRemoveError(error.message);
      } else {
        setRemoveError('Failed to remove collaborator. Please try again.');
      }
    } finally {
      setIsRemoving(false);
    }
  }

  const handleCloseRemoveModal = () => {
    if (!isRemoving) {
      setShowRemoveModal(false);
      setCollaboratorToRemove(null);
      setRemoveError('');
    }
  }

  const handleSave = async (permissions: ContainersPermissionInput) => {
    setIsFormSubmitting(true);

    try {
      if (isEmailValid) {
        // Email is valid, add collaborator directly
        await back4app2.addCollaborator(props.appId, newCollaboratorEmail, permissions as ContainersPermission);
      } else {
        // Email is not valid, send invite
        await back4app2.sendInviteToCollaborator(props.appId, newCollaboratorEmail, permissions as ContainersPermission);
      }

      // List will be updated automatically via subscription

      // Show success message
      const successMessage = isEmailValid 
        ? 'Collaborator added successfully!'
        : 'Invitation sent successfully!';
      toast.success(successMessage, {
        className: "bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />
      });
    } catch (error) {
      if (error instanceof CollaboratorError) {
        setEmailError(error.message);
      }
    } finally {
      setIsFormSubmitting(false);
      setShowPermissionModal(false);
      setNewCollaboratorEmail('');
      setShowInvite(false);
      setIsEmailValid(false);
    }
  }
  
  return (
    <>
      <div className="my-6">
      <div className="text-[1.125rem] font-semibold leading-140 mb-2">Collaborators</div>
      {props.isOwner && (
        <>
        <div className="text-light-grey font-medium mb-6">Collaborators can do everything, with the exception of deleting or suspending it.</div>
          <div className="flex items-center gap-2">
            <TextInput 
              type="email" 
              value={newCollaboratorEmail} 
              onChange={handleEmailChange} 
              placeholderText="Collaborator's email" 
              label="Add new collaborator"
              isDisabled={!props.isOwner}
            />
            <Button type="secondary" className="mt-8 py-4 text-cta-green border border-cta-green rounded-md text-xs hover:font-normal" onClick={handleAddCollaborator} isDisabled={disableAddButton}>{'Add'}</Button>
          </div>
          {emailError && (
            <div className="text-xs bg-system-red text-white px-4 py-1 my-1 rounded-sm">{emailError}</div>
          )}
          {showInvite && (
            <div className="flex justify-center items-center bg-system-red text-white px-4 py-1 mt-1 rounded-sm text-xs">
              Email is not registered on Back4App - {' '}
              <Button 
                type="secondary" 
                className="text-white border border-white rounded-md text-xs hover:font-normal ml-2 py-1 px-2" 
                onClick={() => {
                  setIsEmailValid(false);
                  setShowPermissionModal(true);
                }}
              >
                Send Invite
              </Button>
            </div>
          )}
        </>
      )}
      <CollaboratorPermissionModal
        open={showPermissionModal}
        email={newCollaboratorEmail}
        onClose={() => {
          setShowPermissionModal(false);
          setIsEmailValid(false);
        }}
        onConfirm={handleSave}
        defaultPermissions={defaultFeaturesPermissions}
        customPermissions={customPermissions}
        isSubmitting={isFormSubmitting}
      />
      <CollaboratorPermissionModal
        open={showEditModal}
        email={collaboratorToEdit?.email || ''}
        onClose={handleCloseEditModal}
        onConfirm={handleSaveEdit}
        defaultPermissions={defaultFeaturesPermissions}
        customPermissions={collaboratorToEdit ? convertToContainersPermissionInput(collaboratorToEdit.permissions) : undefined}
        error={editError}
        isSubmitting={isEditing}
      />
      <CollaboratorsList
        collaborators={collaborators}
        isAppOwner={props.isOwner}
        onRemove={handleRemoveCollaborator}
        onEdit={handleEditCollaborator}
        currentUserEmail={props.currentUser.username}
      />
      {isLoadingCollaborators && (
        <div className="mt-6 flex items-center flex-col text-xs text-light-grey gap-2 justify-center"><LoadingSpinner width='1rem' height='1rem' /> Loading collaborators...</div>
      )}
      <RemoveCollaboratorModal
        open={showRemoveModal}
        collaborator={collaboratorToRemove}
        isRemoving={isRemoving}
        error={removeError}
        onClose={handleCloseRemoveModal}
        onConfirm={handleConfirmRemove}
      />
    </div>
    </>
  )
}

export default Collaborators